import {
	Component,
	ElementRef,
	Input,
	TemplateRef,
	ViewChild,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	ViewRef,
	OnDestroy,
	AfterContentInit,
	ViewEncapsulation,
} from '@angular/core';
import { ContentObserver } from '@angular/cdk/observers';
import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

export type FnButtonType =
	| 'primary'
	| 'success'
	| 'danger'
	| 'default'
	| 'link'
	| 'orange'
	| 'secondary'
	| 'dark'
	| 'info'
	| 'warning'
	| 'outline-orange'
	| 'outline-danger'
	| 'outline-primary'
	| 'outline-success'
	| 'outline-info'
	| 'outline-warning'
	| 'outline-secondary'
	| 'outline-dark';
export type FnButtonShape = 'circle' | 'round' | null;

/**
 * This component is a custom HTML button component.
 * It has several built-in features such as support for icons, predefined styles,
 * different button types, different button sizes, and UI customization.
 * To use this button component import FnButtonModule in Module.
 */
@Component({
	selector: 'fn-button',
	templateUrl: './fn-button-component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class FnButtonComponent implements OnDestroy, AfterContentInit {
	/**
	 * Input member
	 * used to set the id of button tag
	 */
	@Input() id = '';

	/**
	 * Input member type of FnButtonType
	 * used to create type of button
	 */
	@Input() type: FnButtonType = 'default';
	/**
	 * Input  member of FnButtonShape
	 * used to set the sape of button
	 */
	@Input() shape: FnButtonShape = null;

	/**
	 * Input  member variable
	 * used to set the text shown inside the button component
	 */
	@Input() text = '';

	/**
	 * Input member variable
	 * used to insert a icon before button value
	 *
	 */
	@Input() iconAddonBefore = '';

	/**
	 * Input member variable
	 * used to insert a icon  button value
	 *
	 */
	@Input() iconAddonAfter = '';

	/**
	 * Input  member variable
	 * used to set the height of button
	 */
	@Input() height = '';
	/**
	 * Input  member variable
	 * used to disabled the button if true
	 */
	@Input() disabled: boolean = null;
	/**
	 * Input member variable
	 * used to add Custom Template Content inside Button .
	 */
	@Input() template: TemplateRef<any>;
	/**
	 * Input  member variable
	 * if true then overstep the boundaries of the element to apper the outside of it
	 */
	@Input() outline = false;
	/**
	 * Input member variable
	 * if true then show Text loading data and disable fnbutton view child
	 */
	@Input() isLoading = false;

	/**
	 * Input member variable
	 */
	@Input() indexShortcut = 0;

	/**
	 * Input member variable
	 * used to add custom css classes on button .
	 */
	@Input() btnClass = '';

	@Input() btnType: 'submit' | 'button' | 'reset' = 'submit';
	/**
	 * Input member variable
	 * used to add custom css classes on button .
	 */
	@Input() contentClass = '';
	/**
	 * Input member function set
	 * setter metted of _textLoading
	 */
	@Input() set textLoading(text) {
		this._textLoading = text;
	}
	/**
	 * Member function
	 * getter metted of _textLoading
	 */
	get textLoading() {
		return this._textLoading;
	}

	// @Output() click: EventEmitter<any> = new EventEmitter<any>();

	/**
	 * View child of fn button component
	 */
	@ViewChild('fnbutton', { static: true }) buttonElement: ElementRef;
	/**
	 * Private variable  fn button component
	 * used to show text on button when isLoading is True
	 * its Value set by set and get method
	 */
	private _textLoading: string;
	/**
	 * Destroy$  of fn button component
	 */
	private destroy$ = new Subject();
	/**
	 * Creates an instance of fn button component.
	 * @param elementRef ElementRef object
	 * @param cdr ChangeDetectorRef Object to detect changes
	 * @param contentObserver ContentObserver service Object for watching elements for changes to their content.
	 */
	constructor(
		private elementRef: ElementRef,
		private cdr: ChangeDetectorRef,
		private contentObserver: ContentObserver
	) {}
	/**
	 * Implements ngAfterContentInit event handler
	 */
	ngAfterContentInit() {
		this.contentObserver
			.observe(this.buttonElement)
			.pipe(startWith(true), takeUntil(this.destroy$))
			.subscribe(() => {
				Promise.resolve().then(() => this.checkContent());
			});
	}
	/**
	 * Implements ngOnDestroy event handler.
	 */

	ngOnDestroy(): void {
		this.destroy$.next(0);
		this.destroy$.complete();
	}

	/**
	 * member method to detect change when viewRef is not distroyed.
	 */
	checkContent(): void {
		if (!(this.cdr as ViewRef).destroyed) {
			this.cdr.detectChanges();
		}
	}
}
